import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';
import { getImageUrl, formatPrice } from '../utils/helpers';
import { trackBeginCheckout } from '../utils/analytics';

const Cart = () => {
  const { cart, removeFromCart, updateQuantity, getCartTotal, clearCart } = useCart();
  const navigate = useNavigate();

  const handleCheckout = () => {
    trackBeginCheckout(cart, getCartTotal());
    navigate('/checkout');
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Card className="p-12 text-center max-w-md">
          <ShoppingBag className="w-24 h-24 mx-auto text-gray-300 mb-4" />
          <h2 className="text-2xl font-bold mb-2">Tu carrito está vacío</h2>
          <p className="text-gray-600 mb-6">
            ¡Descubre miles de productos y comienza a comprar!
          </p>
          <Button onClick={() => navigate('/')} className="bg-zl-orange hover:bg-zl-orange-600">
            Ir a comprar
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <h1 className="text-3xl font-bold mb-6">Carrito de compras</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.map((item) => (
              <Card key={item.key || item.id} className="p-4">
                <div className="flex flex-wrap sm:flex-nowrap gap-3">
                  <img
                    src={getImageUrl(item.image)}
                    alt={item.title}
                    className="w-20 h-20 sm:w-32 sm:h-32 object-contain rounded shrink-0"
                    onError={(e) => e.target.src = 'https://via.placeholder.com/128'}
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                    {item.variant_label && (
                      <p className="text-sm text-gray-700 mb-1" data-testid={`cart-item-variant-${item.key || item.id}`}>Variante: <span className="font-medium">{item.variant_label}</span></p>
                    )}
                    <p className="text-gray-600 text-sm mb-2">
                      Vendedor: {item.seller}
                    </p>
                    {item.freeShipping && (
                      <p className="text-green-600 text-sm font-medium mb-3">
                        Envío gratis
                      </p>
                    )}

                    <div className="flex flex-wrap gap-3 items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => updateQuantity(item.key || item.id, item.quantity - 1)}
                          data-testid={`cart-minus-${item.key || item.id}`} aria-label="Reducir cantidad"
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <span className="font-semibold w-8 text-center">{item.quantity}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => updateQuantity(item.key || item.id, item.quantity + 1)}
                          data-testid={`cart-plus-${item.key || item.id}`} aria-label="Aumentar cantidad"
                          disabled={item.quantity >= item.stock}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>

                      <div className="text-right">
                        <p className="text-2xl font-bold">
                          {formatPrice(item.price * item.quantity)}
                        </p>
                        {item.quantity > 1 && (
                          <p className="text-sm text-gray-600">
                            {formatPrice(item.price)} c/u
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeFromCart(item.key || item.id)}
                    data-testid={`cart-remove-${item.key || item.id}`} aria-label="Eliminar producto"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-5 h-5" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>

          {/* Order Summary */}
          <div>
            <Card className="p-6 sticky top-24">
              <h2 className="text-xl font-bold mb-4">Resumen de compra</h2>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-600">Productos ({cart.length})</span>
                  <span className="font-semibold">{formatPrice(getCartTotal())}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Envío</span>
                  <span className="font-semibold text-green-600">Gratis</span>
                </div>
                <div className="border-t pt-3">
                  <div className="flex justify-between text-xl">
                    <span className="font-bold">Total</span>
                    <span className="font-bold text-zl-orange">
                      {formatPrice(getCartTotal())}
                    </span>
                  </div>
                </div>
              </div>

              <Button
                className="w-full bg-zl-orange hover:bg-zl-orange-600 text-white text-lg h-12 mb-3"
                onClick={handleCheckout}
                data-testid="cart-checkout"
              >
                Continuar compra
              </Button>

              <div className="text-center text-sm text-gray-600 mb-4">
                <p>Aceptamos todos los medios de pago</p>
              </div>

              <div className="flex justify-center space-x-2 text-2xl">
                <span>💳</span>
                <span>💰</span>
                <span>🏦</span>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
